/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
    NgModule,
    ComponentRef,
    Directive,
    EmbeddedViewRef,
    TemplateRef,
    ComponentFactoryResolver,
    ViewContainerRef,
    OnDestroy,
    Input,
} from '@angular/core';
import {Portal, TemplatePortal, ComponentPortal, BasePortalHost} from './portal';
/**
 * Directive version of a `TemplatePortal`. Because the directive *is* a TemplatePortal,
 * the directive instance itself can be attached to a host, enabling declarative use of portals.
 * 
 * Usage:
 * <ng-template portal #greeting>
 *   <p> Hello {{name}} </p>
 * </ng-template>
 */
export class TemplatePortalDirective extends TemplatePortal<any> {
/**
 * @param {?} templateRef
 * @param {?} viewContainerRef
 */
constructor(templateRef: TemplateRef<any>, viewContainerRef: ViewContainerRef) {
    super(templateRef, viewContainerRef);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdk-portal], [cdkPortal], [portal]',
  exportAs: 'cdkPortal',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: TemplateRef, },
{type: ViewContainerRef, },
];
}

function TemplatePortalDirective_tsickle_Closure_declarations() {
/** @type {?} */
TemplatePortalDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
TemplatePortalDirective.ctorParameters;
}

/**
 * Directive version of a PortalHost. Because the directive *is* a PortalHost, portals can be
 * directly attached to it, enabling declarative use.
 * 
 * Usage:
 * <ng-template [cdkPortalHost]="greeting"></ng-template>
 */
export class PortalHostDirective extends BasePortalHost implements OnDestroy {
/**
 * The attached portal.
 */
private _portal: Portal<any> | null = null;
/**
 * @param {?} _componentFactoryResolver
 * @param {?} _viewContainerRef
 */
constructor(
private _componentFactoryResolver: ComponentFactoryResolver,
private _viewContainerRef: ViewContainerRef) {
    super();
  }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedPortal() { return this.portal; }
/**
 * @param {?} v
 * @return {?}
 */
set _deprecatedPortal(v) { this.portal = v; }
/**
 * Portal associated with the Portal host.
 * @return {?}
 */
get portal(): Portal<any> | null {
    return this._portal;
  }
/**
 * @param {?} portal
 * @return {?}
 */
set portal(portal: Portal<any> | null) {
    if (this.hasAttached()) {
      super.detach();
    }

    if (portal) {
      super.attach(portal);
    }

    this._portal = portal;
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    super.dispose();
    this._portal = null;
  }
/**
 * Attach the given ComponentPortal to this PortalHost using the ComponentFactoryResolver.
 * 
 * @template T
 * @param {?} portal Portal to be attached to the portal host.
 * @return {?}
 */
attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
    portal.setAttachedHost(this);

    // If the portal specifies an origin, use that as the logical location of the component
    // in the application tree. Otherwise use the location of this PortalHost.
    let /** @type {?} */ viewContainerRef = portal.viewContainerRef != null ?
        portal.viewContainerRef :
        this._viewContainerRef;

    let /** @type {?} */ componentFactory =
        this._componentFactoryResolver.resolveComponentFactory(portal.component);
    let /** @type {?} */ ref = viewContainerRef.createComponent(
        componentFactory, viewContainerRef.length,
        portal.injector || viewContainerRef.parentInjector);

    super.setDisposeFn(() => ref.destroy());
    this._portal = portal;

    return ref;
  }
/**
 * Attach the given TemplatePortal to this PortlHost as an embedded View.
 * @template C
 * @param {?} portal Portal to be attached.
 * @return {?}
 */
attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
    portal.setAttachedHost(this);
    const /** @type {?} */ viewRef = this._viewContainerRef.createEmbeddedView(portal.templateRef, portal.context);
    super.setDisposeFn(() => this._viewContainerRef.clear());

    this._portal = portal;

    return viewRef;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[cdkPortalHost], [portalHost]',
  inputs: ['portal: cdkPortalHost']
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ComponentFactoryResolver, },
{type: ViewContainerRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_deprecatedPortal': [{ type: Input, args: ['portalHost', ] },],
};
}

function PortalHostDirective_tsickle_Closure_declarations() {
/** @type {?} */
PortalHostDirective.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PortalHostDirective.ctorParameters;
/** @type {?} */
PortalHostDirective.propDecorators;
/**
 * The attached portal.
 * @type {?}
 */
PortalHostDirective.prototype._portal;
/** @type {?} */
PortalHostDirective.prototype._componentFactoryResolver;
/** @type {?} */
PortalHostDirective.prototype._viewContainerRef;
}

export class PortalModule {static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  exports: [TemplatePortalDirective, PortalHostDirective],
  declarations: [TemplatePortalDirective, PortalHostDirective],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function PortalModule_tsickle_Closure_declarations() {
/** @type {?} */
PortalModule.decorators;
/**
 * @nocollapse
 * @type {?}
 */
PortalModule.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../src/cdk/portal/portal-directives.ts